import { openDB, type IDBPDatabase } from 'idb';
import { apiClient } from '@/services/api';

/**
 * Motor de outbox (escritas offline).
 *
 * Fila FIFO persistida em IndexedDB: mutações feitas offline são enfileiradas e
 * reexecutadas ("flush") quando a conexão volta. Genérico por `entity` para ser
 * reaproveitado em fases seguintes (clientes/produtos/pedidos); esta fatia só
 * exercita `despesa`.
 */

export type OutboxOp = 'create' | 'update' | 'delete';

export interface OutboxEntry {
  id: string; // id da entrada na fila (crypto.randomUUID)
  entity: string; // 'despesa' | 'cliente' | 'produto' | 'pedido'
  op: OutboxOp;
  method: 'POST' | 'PUT' | 'DELETE';
  path: string; // ex.: '/despesas' | '/despesas/tmp_xxx'
  body?: unknown;
  targetId?: string; // id (real ou 'tmp_') que a operação afeta
  createdAt: number;
  attempts: number;
  status: 'pending' | 'error';
  lastError?: string;
}

export type EnqueueInput = Pick<OutboxEntry, 'entity' | 'op' | 'method' | 'path'> &
  Partial<Pick<OutboxEntry, 'body' | 'targetId'>>;

export interface OutboxSnapshot {
  count: number; // nº de entradas pendentes
  pendingIds: Set<string>; // targetIds pendentes
  errorIds: Set<string>; // targetIds em erro (dead-letter)
}

// -- Storage abstrato: permite injetar uma implementação em memória nos testes,
//    evitando depender de IndexedDB (ausente no jsdom). --------------------------
export interface OutboxStore {
  getAll(): Promise<OutboxEntry[]>;
  put(entry: OutboxEntry): Promise<void>;
  delete(id: string): Promise<void>;
  clear(): Promise<void>;
  // Mapa de ids temporários (tmp_) → ids reais, preenchido conforme as criações
  // sincronizam. Persistido para sobreviver a sincronizações parciais entre sessões.
  getIdMap(): Promise<Record<string, string>>;
  putIdMapping(tmp: string, real: string): Promise<void>;
  clearIdMap(): Promise<void>;
}

const DB_NAME = 'erplight-outbox';
const STORE_NAME = 'entries';
const IDMAP_STORE = 'idmap';

let dbPromise: Promise<IDBPDatabase> | null = null;
function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, 2, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
        // Store de chaves out-of-line: chave = tmp id, valor = id real.
        if (!db.objectStoreNames.contains(IDMAP_STORE)) {
          db.createObjectStore(IDMAP_STORE);
        }
      },
    });
  }
  return dbPromise;
}

const idbStore: OutboxStore = {
  async getAll() {
    return (await getDb()).getAll(STORE_NAME) as Promise<OutboxEntry[]>;
  },
  async put(entry) {
    await (await getDb()).put(STORE_NAME, entry);
  },
  async delete(id) {
    await (await getDb()).delete(STORE_NAME, id);
  },
  async clear() {
    await (await getDb()).clear(STORE_NAME);
  },
  async getIdMap() {
    const db = await getDb();
    const keys = (await db.getAllKeys(IDMAP_STORE)) as string[];
    const values = (await db.getAll(IDMAP_STORE)) as string[];
    const map: Record<string, string> = {};
    keys.forEach((k, i) => (map[k] = values[i]));
    return map;
  },
  async putIdMapping(tmp, real) {
    await (await getDb()).put(IDMAP_STORE, real, tmp);
  },
  async clearIdMap() {
    await (await getDb()).clear(IDMAP_STORE);
  },
};

export function createMemoryStore(): OutboxStore {
  const map = new Map<string, OutboxEntry>();
  const idmap = new Map<string, string>();
  return {
    async getAll() {
      return [...map.values()];
    },
    async put(entry) {
      map.set(entry.id, entry);
    },
    async delete(id) {
      map.delete(id);
    },
    async clear() {
      map.clear();
    },
    async getIdMap() {
      return Object.fromEntries(idmap);
    },
    async putIdMapping(tmp, real) {
      idmap.set(tmp, real);
    },
    async clearIdMap() {
      idmap.clear();
    },
  };
}

// Em produção (navegador) usa IndexedDB; em ambientes sem ele (jsdom nos
// testes, SSR) cai para o store em memória, evitando quebrar na abertura do DB.
let store: OutboxStore = typeof indexedDB !== 'undefined' ? idbStore : createMemoryStore();

// -- Assinantes (contagem/badges) e ouvintes de flush (resync da UI) -----------
const snapshotListeners = new Set<(s: OutboxSnapshot) => void>();
const flushedListeners = new Set<(entity: string) => void>();

async function buildSnapshot(): Promise<OutboxSnapshot> {
  const all = await store.getAll();
  const pendingIds = new Set<string>();
  const errorIds = new Set<string>();
  let count = 0;
  for (const e of all) {
    if (e.status === 'error') {
      if (e.targetId) errorIds.add(e.targetId);
    } else {
      count += 1;
      if (e.targetId) pendingIds.add(e.targetId);
    }
  }
  return { count, pendingIds, errorIds };
}

async function notify(): Promise<void> {
  const snap = await buildSnapshot();
  snapshotListeners.forEach((l) => l(snap));
}

export async function getAll(): Promise<OutboxEntry[]> {
  return store.getAll();
}

export async function getPending(): Promise<OutboxEntry[]> {
  return (await store.getAll()).filter((e) => e.status === 'pending');
}

export function subscribe(listener: (s: OutboxSnapshot) => void): () => void {
  snapshotListeners.add(listener);
  // Emite o estado atual de forma assíncrona.
  void buildSnapshot().then((s) => listener(s));
  return () => snapshotListeners.delete(listener);
}

export function onFlushed(listener: (entity: string) => void): () => void {
  flushedListeners.add(listener);
  return () => flushedListeners.delete(listener);
}

// Notifica quando uma entrada é descartada por erro permanente (dead-letter) no
// flush — ex.: replay de venda que falha 400. Usado para um aviso global.
export interface DeadLetterInfo {
  entity: string;
  message: string;
}
const deadLetterListeners = new Set<(info: DeadLetterInfo) => void>();
export function onDeadLetter(listener: (info: DeadLetterInfo) => void): () => void {
  deadLetterListeners.add(listener);
  return () => deadLetterListeners.delete(listener);
}
function emitDeadLetter(entity: string, message: string): void {
  deadLetterListeners.forEach((l) => l({ entity, message }));
}

// -- Remapeamento de ids temporários (tmp_ → real) ------------------------------
// Um id temporário é `tmp_` + UUID. Restringimos a esse formato para não
// confundir com texto do usuário que por acaso contenha "tmp_".
const TMP_ID_RE = /tmp_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** Substitui, recursivamente, strings-folha que são chave do mapa pelo id real. */
export function resolveTmpInBody(value: unknown, map: Record<string, string>): unknown {
  if (typeof value === 'string') return map[value] ?? value;
  if (Array.isArray(value)) return value.map((v) => resolveTmpInBody(v, map));
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = resolveTmpInBody(v, map);
    return out;
  }
  return value;
}

/** Reescreve ids temporários presentes no path pelos reais. */
export function resolveTmpInPath(path: string, map: Record<string, string>): string {
  let p = path;
  for (const [tmp, real] of Object.entries(map)) {
    if (p.includes(tmp)) p = p.split(tmp).join(real);
  }
  return p;
}

/** Detecta se ainda há alguma referência a id temporário não resolvida. */
export function hasUnresolvedTmpId(value: unknown): boolean {
  if (typeof value === 'string') return TMP_ID_RE.test(value);
  if (Array.isArray(value)) return value.some((v) => hasUnresolvedTmpId(v));
  if (value && typeof value === 'object') {
    return Object.values(value).some((v) => hasUnresolvedTmpId(v));
  }
  return false;
}

// -- Enfileiramento com coalescing por (entity + targetId) ----------------------
// Mantém a fila mínima e correta: um create seguido de update funde no create;
// create seguido de delete vira no-op; update+delete mantém só o delete.
export async function enqueue(input: EnqueueInput): Promise<void> {
  const all = await store.getAll();
  const siblings = input.targetId
    ? all.filter(
        (e) => e.entity === input.entity && e.targetId === input.targetId && e.status === 'pending'
      )
    : [];
  const existingCreate = siblings.find((e) => e.op === 'create');
  const existingUpdate = siblings.find((e) => e.op === 'update');

  if (input.op === 'update') {
    if (existingCreate) {
      existingCreate.body = { ...(existingCreate.body as object), ...(input.body as object) };
      await store.put(existingCreate);
      await notify();
      return;
    }
    if (existingUpdate) {
      existingUpdate.body = { ...(existingUpdate.body as object), ...(input.body as object) };
      existingUpdate.path = input.path;
      await store.put(existingUpdate);
      await notify();
      return;
    }
  }

  if (input.op === 'delete') {
    // Remove qualquer create/update pendente do mesmo alvo.
    for (const s of siblings) await store.delete(s.id);
    if (existingCreate) {
      // Nunca chegou ao servidor: create + delete = no-op.
      await notify();
      return;
    }
  }

  const entry: OutboxEntry = {
    id: crypto.randomUUID(),
    entity: input.entity,
    op: input.op,
    method: input.method,
    path: input.path,
    body: input.body,
    targetId: input.targetId,
    createdAt: Date.now(),
    attempts: 0,
    status: 'pending',
  };
  await store.put(entry);
  await notify();
}

// -- Flush (replay) -------------------------------------------------------------
function responseStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}
function errorMessage(err: unknown): string {
  return (err as { message?: string })?.message || 'erro desconhecido';
}

let flushing = false;

export async function flush(): Promise<void> {
  if (flushing) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  flushing = true;
  try {
    const pending = (await store.getAll())
      .filter((e) => e.status === 'pending')
      .sort((a, b) => a.createdAt - b.createdAt);

    const flushedEntities = new Set<string>();

    for (const entry of pending) {
      // Remapeia ids temporários (cliente/produto criados offline) pelos reais já
      // sincronizados. Ordem FIFO garante que as criações vêm antes dos dependentes.
      const idMap = await store.getIdMap();
      const resolvedPath = resolveTmpInPath(entry.path, idMap);
      const resolvedBody = resolveTmpInBody(entry.body, idMap);

      // Sobrou referência a um tmp id: a criação da qual esta entrada depende não
      // sincronizou (virou dead-letter). Não adianta enviar — cascata de erro.
      if (hasUnresolvedTmpId(resolvedBody) || TMP_ID_RE.test(resolvedPath)) {
        entry.status = 'error';
        entry.lastError = 'dependência não sincronizada';
        entry.attempts += 1;
        await store.put(entry);
        await notify();
        emitDeadLetter(entry.entity, entry.lastError);
        continue;
      }

      try {
        const resp = await apiClient.request({
          method: entry.method,
          url: resolvedPath,
          data: resolvedBody,
        });
        // Captura o id real gerado pelo backend para remapear os dependentes.
        if (entry.op === 'create' && entry.targetId?.startsWith('tmp_')) {
          const realId = (resp as { data?: { id?: string } })?.data?.id;
          if (realId) await store.putIdMapping(entry.targetId, realId);
        }
        await store.delete(entry.id);
        flushedEntities.add(entry.entity);
        await notify();
      } catch (err) {
        const status = responseStatus(err);
        if (status === undefined) {
          // Sem resposta = ainda offline: para o flush e mantém a fila.
          break;
        }
        entry.attempts += 1;
        entry.lastError = errorMessage(err);
        if (status >= 400 && status < 500) {
          // Erro permanente (validação/permissão/conflito): dead-letter.
          entry.status = 'error';
          await store.put(entry);
          await notify();
          emitDeadLetter(entry.entity, entry.lastError);
          continue;
        }
        // 5xx: mantém pendente para nova tentativa e para o flush.
        await store.put(entry);
        await notify();
        break;
      }
    }

    flushedEntities.forEach((entity) => flushedListeners.forEach((l) => l(entity)));
  } finally {
    flushing = false;
  }
}

let initialized = false;

export function initOutbox(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  window.addEventListener('online', () => {
    void flush();
  });
  if (navigator.onLine) void flush();
}

// -- Somente para testes --------------------------------------------------------
export function __resetForTests(nextStore?: OutboxStore): void {
  store = nextStore ?? createMemoryStore();
  snapshotListeners.clear();
  flushedListeners.clear();
  deadLetterListeners.clear();
  flushing = false;
  initialized = false;
}
